import { ApiController } from '@/services/ApiController';
import { ApiRoutes } from '@/enums/api/ApiRoutes';
import { AxiosResponse } from 'axios';
import { MontageTeamCreatePayload, MontageTeamUpdatePayload } from '@/interfaces/IMontageTeam';

/**
 * MontageTeamAPI - API controller for montage teams
 * Handles all CRUD operations for montage teams
 */
export default class MontageTeamAPI {
    /**
     * Get all montage teams
     * GET /montage-teams
     */
    public static async getAll(): Promise<AxiosResponse> {
        try {
            const response = await ApiController.get(ApiRoutes.montageTeams.getAll);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Get a single montage team by ID
     * GET /montage-teams/{id}
     */
    public static async getById(id: string): Promise<AxiosResponse> {
        try {
            const response = await ApiController.get(ApiRoutes.montageTeams.getById(id));
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Create a new montage team
     * POST /montage-teams
     */
    public static async create(payload: MontageTeamCreatePayload): Promise<AxiosResponse> {
        const { vehicleId, ...attributes } = payload;

        const request: any = {
            data: {
                attributes,
                type: 'montage_teams',
                relationships: {},
            },
        };

        if (vehicleId) {
            request.data.relationships.vehicle = {
                data: {
                    type: 'vehicles',
                    id: vehicleId,
                },
            };
        }

        try {
            const response = await ApiController.post(ApiRoutes.montageTeams.create, request);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Update an existing montage team
     * PATCH /montage-teams/{id}
     */
    public static async update(id: string, payload: MontageTeamUpdatePayload): Promise<AxiosResponse> {
        const { vehicleId, ...attributes } = payload;

        const request: any = {
            data: {
                attributes,
                type: 'montage_teams',
                id,
                relationships: {},
            },
        };

        if (vehicleId) {
            request.data.relationships.vehicle = {
                data: {
                    type: 'vehicles',
                    id: vehicleId,
                },
            };
        } else {
            request.data.relationships.vehicle = {
                data: null,
            };
        }

        try {
            const response = await ApiController.patch(ApiRoutes.montageTeams.update(id), request);
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Delete a montage team
     * DELETE /montage-teams/{id}
     */
    public static async delete(id: string): Promise<AxiosResponse> {
        try {
            const response = await ApiController.delete(ApiRoutes.montageTeams.delete(id));
            return response as AxiosResponse;
        } catch (e) {
            return Promise.reject(e);
        }
    }
}
